import React, { useEffect, useState } from "react"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import Modal  from "@react/shared/ui/Modal"
import Button from "@react/shared/ui/Button"
import { createSubcontract, updateSubcontract, fetchSuppliers } from "../api/subcontracts"

const toEuros = cents => (cents == null ? "" : (cents / 100).toString().replace(".", ","))
const toEurosFloat = value => {
  if (value == null || value === "") return 0
  const normalized = String(value).replace(/\s/g, "").replace(",", ".")
  const n = parseFloat(normalized)
  return isNaN(n) ? 0 : n
}

const SubcontractModal = ({ isOpen, mode, projectId, subcontract, suppliersUrl, onClose, onSaved }) => {
  const [suppliers,  setSuppliers]  = useState([])
  const [supplierId, setSupplierId] = useState("")
  const [title,      setTitle]      = useState("")
  const [description, setDescription] = useState("")
  const [amount,     setAmount]     = useState("")
  const [paid,       setPaid]       = useState("0")
  const [retention,  setRetention]  = useState("5")
  const [bankGuarantee, setBankGuarantee] = useState(false)
  const [bankRef,    setBankRef]    = useState("")
  const [autolVat,   setAutolVat]   = useState(false)
  const [declaredAt, setDeclaredAt] = useState("")
  const [directPayment,       setDirectPayment]       = useState(false)
  const [directPaymentAmount, setDirectPaymentAmount] = useState("")
  const [moaAgreedAt,         setMoaAgreedAt]         = useState("")
  const [moaAgreementRef,     setMoaAgreementRef]     = useState("")
  const [saving,     setSaving]     = useState(false)
  const [errors,     setErrors]     = useState([])

  useEffect(() => {
    if (!isOpen) return
    setErrors([])
    if (mode === "edit" && subcontract) {
      setSupplierId(String(subcontract.supplier?.id || ""))
      setTitle(subcontract.title || "")
      setDescription(subcontract.description || "")
      setAmount(toEuros(subcontract.contract_amount?.cents))
      setPaid(toEuros(subcontract.paid_amount?.cents))
      setRetention(String(subcontract.retention_percentage || 0))
      setBankGuarantee(!!subcontract.bank_guarantee_enabled)
      setBankRef(subcontract.bank_guarantee_reference || "")
      setAutolVat(!!subcontract.auto_liquidation_vat)
      setDeclaredAt(subcontract.declared_at || "")
      setDirectPayment(!!subcontract.direct_payment_enabled)
      setDirectPaymentAmount(toEuros(subcontract.direct_payment_amount_cents))
      setMoaAgreedAt(subcontract.moa_agreed_at || "")
      setMoaAgreementRef(subcontract.moa_agreement_reference || "")
    } else {
      setSupplierId(""); setTitle(""); setDescription("")
      setAmount(""); setPaid("0"); setRetention("5")
      setBankGuarantee(false); setBankRef(""); setAutolVat(false); setDeclaredAt("")
      setDirectPayment(false); setDirectPaymentAmount("")
      setMoaAgreedAt(""); setMoaAgreementRef("")
    }

    if (suppliersUrl && suppliers.length === 0) {
      fetchSuppliers(suppliersUrl)
        .then(payload => setSuppliers(payload.suppliers || []))
        .catch(() => {})
    }
  }, [isOpen, mode, subcontract, suppliersUrl])

  const handleSave = () => {
    if (saving) return
    if (!supplierId && mode !== "edit") {
      setErrors([I18n.t("projects.subcontracts.errors.supplier_required")])
      return
    }
    if (!title) {
      setErrors([I18n.t("projects.subcontracts.errors.title_required")])
      return
    }

    setSaving(true)
    setErrors([])

    const body = {
      title,
      description,
      retention_percentage:     parseFloat(String(retention).replace(",", ".")) || 0,
      bank_guarantee_enabled:   bankGuarantee,
      bank_guarantee_reference: bankRef,
      auto_liquidation_vat:     autolVat,
      declared_at:              declaredAt || null,
      amount_euros:             toEurosFloat(amount),
      paid_euros:               toEurosFloat(paid),
      direct_payment_enabled:   directPayment,
      direct_payment_euros:     directPayment ? toEurosFloat(directPaymentAmount) : 0,
      moa_agreed_at:            moaAgreedAt || null,
      moa_agreement_reference:  moaAgreementRef || null,
    }
    if (mode !== "edit") body.supplier_id = supplierId

    const request = (mode === "edit" && subcontract)
      ? updateSubcontract(projectId, subcontract.id, body)
      : createSubcontract(projectId, body)

    request
      .then(response => {
        if (response?.errors) {
          setErrors(Array.isArray(response.errors) ? response.errors : [String(response.errors)])
          return
        }
        onSaved && onSaved(response)
        onClose()
      })
      .catch(() => setErrors([I18n.t("projects.subcontracts.errors.network")]))
      .finally(() => setSaving(false))
  }

  const footer = (
    <>
      <div />
      <div className="flex items-center gap-2">
        <Button variant="ghost"   onClick={onClose}>{I18n.t("projects.subcontracts.form.cancel")}</Button>
        <Button variant="primary" onClick={handleSave} disabled={saving}>
          <FontAwesomeIcon icon={saving ? "spinner" : "check"} spin={saving} />
          {mode === "edit"
            ? I18n.t("projects.subcontracts.form.save")
            : I18n.t("projects.subcontracts.form.create")}
        </Button>
      </div>
    </>
  )

  return (
    <Modal
      isOpen  = {isOpen}
      onClose = {onClose}
      eyebrow = {mode === "edit"
        ? I18n.t("projects.subcontracts.form.eyebrow_edit", { number: subcontract?.number || "" })
        : I18n.t("projects.subcontracts.form.eyebrow_new")}
      title   = {mode === "edit"
        ? (subcontract?.title || I18n.t("projects.subcontracts.form.title_fallback"))
        : I18n.t("projects.subcontracts.form.title_new")}
      footer  = {footer}
    >
      <div className="grid grid-cols-12 gap-4">
        {mode !== "edit" && (
          <div className="col-span-12">
            <label className="block text-xs font-medium text-slate-700 mb-1.5">
              {I18n.t("projects.subcontracts.form.supplier")} <span className="text-rose-500">*</span>
            </label>
            <select
              value     = {supplierId}
              onChange  = {e => setSupplierId(e.target.value)}
              className = "w-full h-10 px-3 rounded-md border border-slate-200 text-sm bg-white"
            >
              <option value="">{I18n.t("projects.subcontracts.form.supplier_placeholder")}</option>
              {suppliers.map(s => (
                <option key={s.id} value={s.id}>{s.name}</option>
              ))}
            </select>
          </div>
        )}

        <div className="col-span-8">
          <label className="block text-xs font-medium text-slate-700 mb-1.5">
            {I18n.t("projects.subcontracts.form.field_title")} <span className="text-rose-500">*</span>
          </label>
          <input
            value     = {title}
            onChange  = {e => setTitle(e.target.value)}
            placeholder = {I18n.t("projects.subcontracts.form.title_placeholder")}
            className = "w-full h-10 px-3 rounded-md border border-slate-200 text-sm"
          />
        </div>

        <div className="col-span-4">
          <label className="block text-xs font-medium text-slate-700 mb-1.5">
            {I18n.t("projects.subcontracts.form.amount")} <span className="text-rose-500">*</span>
          </label>
          <div className="relative">
            <input
              value     = {amount}
              onChange  = {e => setAmount(e.target.value)}
              placeholder = "0,00"
              className = "w-full h-10 px-3 pr-10 rounded-md border border-slate-200 text-sm text-right tabular-nums"
            />
            <span className="absolute inset-y-0 right-0 pr-3 inline-flex items-center text-slate-500 text-sm pointer-events-none">€</span>
          </div>
        </div>

        <div className="col-span-12">
          <label className="block text-xs font-medium text-slate-700 mb-1.5">
            {I18n.t("projects.subcontracts.form.description")}
          </label>
          <textarea
            value     = {description}
            onChange  = {e => setDescription(e.target.value)}
            rows      = "3"
            className = "w-full px-3 py-2 rounded-md border border-slate-200 text-sm"
          />
        </div>

        <div className="col-span-4">
          <label className="block text-xs font-medium text-slate-700 mb-1.5">
            {I18n.t("projects.subcontracts.form.paid")}
          </label>
          <div className="relative">
            <input
              value     = {paid}
              onChange  = {e => setPaid(e.target.value)}
              placeholder = "0,00"
              className = "w-full h-10 px-3 pr-10 rounded-md border border-slate-200 text-sm text-right tabular-nums"
            />
            <span className="absolute inset-y-0 right-0 pr-3 inline-flex items-center text-slate-500 text-sm pointer-events-none">€</span>
          </div>
        </div>

        <div className="col-span-4">
          <label className="block text-xs font-medium text-slate-700 mb-1.5">
            {I18n.t("projects.subcontracts.form.retention")}
          </label>
          <div className="relative">
            <input
              type      = "number"
              min       = "0"
              max       = "100"
              step      = "0.5"
              value     = {retention}
              onChange  = {e => setRetention(e.target.value)}
              className = "w-full h-10 px-3 pr-10 rounded-md border border-slate-200 text-sm text-right tabular-nums"
            />
            <span className="absolute inset-y-0 right-0 pr-3 inline-flex items-center text-slate-500 text-sm pointer-events-none">%</span>
          </div>
        </div>

        <div className="col-span-4">
          <label className="block text-xs font-medium text-slate-700 mb-1.5">
            {I18n.t("projects.subcontracts.form.declared_at")}
          </label>
          <input
            type      = "date"
            value     = {declaredAt || ""}
            onChange  = {e => setDeclaredAt(e.target.value)}
            className = "w-full h-10 px-3 rounded-md border border-slate-200 text-sm"
          />
        </div>

        <div className="col-span-12 mt-1 grid grid-cols-12 gap-4">
          <label className="col-span-12 md:col-span-6 flex items-center gap-2 text-[12.5px] text-slate-700">
            <input
              type      = "checkbox"
              checked   = {bankGuarantee}
              onChange  = {e => setBankGuarantee(e.target.checked)}
              className = "h-4 w-4 rounded border-slate-300"
            />
            {I18n.t("projects.subcontracts.form.bank_guarantee")}
          </label>
          <label className="col-span-12 md:col-span-6 flex items-center gap-2 text-[12.5px] text-slate-700">
            <input
              type      = "checkbox"
              checked   = {autolVat}
              onChange  = {e => setAutolVat(e.target.checked)}
              className = "h-4 w-4 rounded border-slate-300"
            />
            {I18n.t("projects.subcontracts.form.auto_liquidation_vat")}
          </label>
        </div>

        {bankGuarantee && (
          <div className="col-span-12">
            <label className="block text-xs font-medium text-slate-700 mb-1.5">
              {I18n.t("projects.subcontracts.form.bank_guarantee_reference")}
            </label>
            <input
              value     = {bankRef}
              onChange  = {e => setBankRef(e.target.value)}
              className = "w-full h-10 px-3 rounded-md border border-slate-200 text-sm font-mono"
            />
          </div>
        )}

        {/* Sous-traitance — paiement direct (art. 1799-1) + agrément MOA (Loi 1975). */}
        <div className="col-span-12 mt-2 p-3 rounded-md border border-slate-200 bg-slate-50/40">
          <label className="flex items-start gap-2.5 text-[12.5px] text-slate-700">
            <input
              type      = "checkbox"
              checked   = {directPayment}
              onChange  = {e => setDirectPayment(e.target.checked)}
              className = "mt-0.5 h-4 w-4 rounded border-slate-300"
            />
            <div className="flex-1">
              <div className="font-semibold text-slate-900">
                {I18n.t("projects.subcontracts.form.direct_payment", { defaultValue: "Paiement direct (art. 1799-1 du Code civil)" })}
              </div>
              <div className="text-[11.5px] text-slate-500 mt-0.5">
                {I18n.t("projects.subcontracts.form.direct_payment_hint", { defaultValue: "Le maître d'ouvrage paie directement ce sous-traitant. Le montant sera déduit automatiquement de chaque situation." })}
              </div>
            </div>
          </label>

          {directPayment && (
            <>
              <div className="mt-3 ml-7 flex items-center gap-3 max-w-sm">
                <label className="text-[12.5px] font-medium text-slate-700 shrink-0">
                  {I18n.t("projects.subcontracts.form.direct_payment_amount", { defaultValue: "Montant HT à régler directement" })}
                </label>
                <div className="relative flex-1">
                  <input
                    value     = {directPaymentAmount}
                    onChange  = {e => setDirectPaymentAmount(e.target.value)}
                    placeholder = "0,00"
                    className = "w-full h-9 px-3 pr-10 rounded-md border border-slate-200 text-sm text-right tabular-nums"
                  />
                  <span className="absolute inset-y-0 right-0 pr-3 inline-flex items-center text-slate-500 text-sm pointer-events-none">€</span>
                </div>
              </div>

              {/* Seuil de déclenchement — 5 000 € HT en marché privé.
                  Avertissement quand le montant est sous le seuil. */}
              {toEurosFloat(directPaymentAmount) > 0 && toEurosFloat(directPaymentAmount) < 5000 && (
                <div className="mt-2 ml-7 px-3 py-1.5 rounded text-[11.5px] bg-amber-50 text-amber-800 border border-amber-200">
                  <FontAwesomeIcon icon="triangle-exclamation" className="mr-1.5 text-[11px]" />
                  {I18n.t("projects.subcontracts.form.direct_payment_threshold_warning")}
                </div>
              )}

              {/* Agrément du maître d'ouvrage — sans agrément, le paiement direct n'est pas opposable. */}
              <div className="mt-3 ml-7 grid grid-cols-2 gap-3 max-w-xl">
                <div>
                  <label className="block text-[12px] font-medium text-slate-700 mb-1">
                    {I18n.t("projects.subcontracts.form.moa_agreed_at", { defaultValue: "Date d'agrément MOA" })}
                  </label>
                  <input
                    type      = "date"
                    value     = {moaAgreedAt || ""}
                    onChange  = {e => setMoaAgreedAt(e.target.value)}
                    className = "w-full h-9 px-3 rounded-md border border-slate-200 text-sm"
                  />
                </div>
                <div>
                  <label className="block text-[12px] font-medium text-slate-700 mb-1">
                    {I18n.t("projects.subcontracts.form.moa_agreement_reference", { defaultValue: "Référence acte d'agrément" })}
                  </label>
                  <input
                    value     = {moaAgreementRef}
                    onChange  = {e => setMoaAgreementRef(e.target.value)}
                    placeholder = {I18n.t("projects.subcontracts.form.moa_agreement_reference_placeholder")}
                    className = "w-full h-9 px-3 rounded-md border border-slate-200 text-sm font-mono"
                  />
                </div>
              </div>

              {/* Conformité — l'agrément MOA est obligatoire (Loi du 31 décembre 1975). */}
              {!moaAgreedAt && (
                <div className="mt-2 ml-7 px-3 py-1.5 rounded text-[11.5px] bg-rose-50 text-rose-800 border border-rose-200">
                  <FontAwesomeIcon icon="circle-exclamation" className="mr-1.5 text-[11px]" />
                  {I18n.t("projects.subcontracts.form.moa_agreement_required")}
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {errors.length > 0 && (
        <div className="mt-4 px-3 py-2 rounded-md bg-rose-50 border border-rose-200 text-rose-700 text-sm">
          {errors.join(", ")}
        </div>
      )}
    </Modal>
  )
}

export default SubcontractModal
